import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { api } from './api';

interface AuthState {
  /** `null` = unknown (still checking), `false` = anon, `true` = authenticated */
  authenticated: boolean | null;
  authenticating: boolean;
  /** True if the most recent auth attempt got a 401 — used to show the "wrong password" message. */
  invalidCredentials: boolean;
  login: (password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  /** Bypass for /access/:password deep links — server set the cookie via redirect/fn, refresh state. */
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const [authenticating, setAuthenticating] = useState(false);
  const [invalidCredentials, setInvalidCredentials] = useState(false);

  const refresh = useCallback(async () => {
    // We don't have a dedicated /whoami endpoint — instead probe /resume.
    // It's the same call we'd make on the portfolio page anyway, so the
    // response will be warm-cached by the time the user navigates there.
    try {
      await api.getResume();
      setAuthenticated(true);
    } catch (err: unknown) {
      if ((err as { status?: number }).status === 401) setAuthenticated(false);
      else setAuthenticated(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(async (password: string) => {
    setAuthenticating(true);
    setInvalidCredentials(false);
    try {
      await api.authenticate(password);
      setAuthenticated(true);
      return true;
    } catch (err: unknown) {
      if ((err as { status?: number }).status === 401) setInvalidCredentials(true);
      setAuthenticated(false);
      return false;
    } finally {
      setAuthenticating(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      /* even if the network call fails, treat the session as gone */
    }
    setAuthenticated(false);
  }, []);

  const value = useMemo<AuthState>(
    () => ({ authenticated, authenticating, invalidCredentials, login, logout, refresh }),
    [authenticated, authenticating, invalidCredentials, login, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
